import { useCurrentAccount, useLogin } from '@application/api';
import { useResetPasswordModal } from '@application/components';
import { ENV, environmentVariable } from '@application/env';
import { useRouter, useShowToast, useValidation } from '@application/utils';
import type { LoginFormProps, LoginFormValues } from '@presentation/components';
import { LoginForm } from '@presentation/components';
import type { TokenResponse } from '@react-oauth/google';
import { useGoogleLogin } from '@react-oauth/google';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { ReactFacebookLoginProps } from 'react-facebook-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import type { SchemaOf } from 'yup';
import { object, string } from 'yup';
import type { LoginFormControllerProps } from './types';

export * from './types';

export const LoginFormController: FC<LoginFormControllerProps> = (props) => {
  const { joinLink, onSuccessfulLogin } = props;

  const { translate } = useRouter();
  const { emailMessage, requiredMessage } = useValidation();
  const { show } = useResetPasswordModal();
  const { showToast } = useShowToast();

  const { loginLoading, loginWithEmail, loginWithFacebookToken, loginWithGoogleToken } = useLogin();

  const { currentAccount, currentAccountLoading } = useCurrentAccount();

  const googleAuthHandler = useCallback(
    (response: TokenResponse) => {
      loginWithGoogleToken(response.access_token);
    },
    [loginWithGoogleToken],
  );

  const facebookAuthHandler: ReactFacebookLoginProps['callback'] = useCallback(
    (response) => {
      const accessToken = (response as any).accessToken as string | undefined;
      loginWithFacebookToken(accessToken);
    },
    [loginWithFacebookToken],
  );

  const formSubmitHandler: LoginFormProps['form']['onSubmit'] = useCallback(
    (values) => {
      if (values.password.length < 6) {
        showToast(translate('common.toasts.apolloError.login/password-too-short'), {
          type: 'error',
        });
        show();
        throw new Error(translate('common.toasts.apolloError.login/password-too-short'));
      } else {
        return loginWithEmail(values);
      }
    },
    [loginWithEmail, show, showToast, translate],
  );

  const signIn = useGoogleLogin({
    onSuccess: googleAuthHandler,
  });

  const validationSchema = useMemo(() => {
    const schema: SchemaOf<LoginFormValues> = object({
      email: string().email(emailMessage).required(requiredMessage),
      password: string().required(requiredMessage),
    }).defined();
    return schema;
  }, [emailMessage, requiredMessage]);

  return (
    <FacebookLogin
      version="4.0"
      appId={environmentVariable(ENV.FACEBOOK_ID)}
      callback={facebookAuthHandler}
      render={(renderProps: ReactFacebookLoginProps) => (
        <LoginForm
          form={{
            onSubmit: formSubmitHandler,
            validationSchema,
            initialValues: {
              email: currentAccount?.email ?? '',
              password: '',
            },
          }}
          onGoogleClick={() => {
            signIn();
          }}
          onForgotPasswordClick={() => {
            show();
          }}
          loading={currentAccountLoading || loginLoading}
          joinLink={joinLink}
          onFacebookClick={renderProps.onClick}
        />
      )}
    />
  );
};
